/**
 * The haul runs a colony's crafting state implies on the plot: which two
 * points a hauler drives between, and where along the way it stands at an
 * instant. Pure, so the rules test without a scene.
 *
 * Two sources. A transfer in flight is one loaded trip between its two pools,
 * timed by the server's own start and landing. A running job whose recipe
 * eats an input is fed by a shuttle from the nearest other building on the
 * plot that makes that input, or from the site pile when none does.
 */

/** A point on the plot, plot-local metres. */
export interface HaulPoint { x: number; z: number; }

/** One structure as the router needs it. */
export interface HaulStop extends HaulPoint {
  halfM: number;
  cell?: string;
  kind?: string;
  buildingId?: string;
}

export type HaulKind = 'transfer' | 'feed';

export interface HaulRoute {
  /** Stable across refreshes, so a hauler keeps its own seed. */
  key: string;
  kind: HaulKind;
  from: HaulPoint;
  to: HaulPoint;
  /** transfer: the trip's own span. feed: when the job began, the shuttle's phase. */
  startedAt: number;
  readyAt: number;
  /** Which side of the straight line the track bows to, -1 or 1. */
  bow: number;
}

export interface HaulEndRef { scope: string; scopeId: string; }

export interface HaulSources {
  stops: readonly HaulStop[];
  transfers: ReadonlyArray<{ id: string; from: HaulEndRef; to: HaulEndRef; startedAt: number; readyAt: number }>;
  jobs: ReadonlyArray<{ id: string; recipeId: string; buildingId: string }>;
  /** Whether a job is running now: not claimed, paused or landed. */
  running(job: { id: string; recipeId: string; buildingId: string }): boolean;
  /** Resource ids a recipe eats. */
  inputsOf(recipeId: string): readonly string[];
  /** Building kinds that run a recipe making this resource. */
  makersOf(resourceId: string): readonly string[];
  /** Which settlement a member hex belongs to. */
  hexSettlement?: Readonly<Record<string, string>>;
  /** The site pile: where crew stock and unmade inputs are drawn from. */
  pile: HaulPoint;
}

/** A hauler's working speed over graded ground, metres per second. */
export const HAUL_SPEED_MS = 9;
/** Seconds a feed shuttle stands at each end, loading or tipping. */
export const HAUL_DWELL_S = 18;
/** Most runs drawn at once; the rest wait for a free hauler. */
export const HAUL_CAP = 24;

function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 100000) / 100000;
}

/** A pad's loading bay: partway from its centre toward the other end, clear of the parts. */
function bay(stop: HaulStop, toward: HaulPoint): HaulPoint {
  const dx = toward.x - stop.x;
  const dz = toward.z - stop.z;
  const d = Math.hypot(dx, dz);
  if (d < 1) return { x: stop.x, z: stop.z };
  const r = Math.min(stop.halfM * 0.62, d * 0.4);
  return { x: stop.x + (dx / d) * r, z: stop.z + (dz / d) * r };
}

function endOf(ref: HaulEndRef, src: HaulSources): HaulStop | HaulPoint | null {
  if (ref.scope === 'PLAYER') return src.pile;
  if (ref.scope === 'SITE') return src.stops.find((s) => s.cell === ref.scopeId) ?? null;
  if (ref.scope === 'SETTLEMENT') {
    return src.stops.find((s) => s.cell !== undefined && src.hexSettlement?.[s.cell] === ref.scopeId) ?? null;
  }
  return null;
}

function isStop(p: HaulStop | HaulPoint): p is HaulStop {
  return 'halfM' in p;
}

function leg(a: HaulStop | HaulPoint, b: HaulStop | HaulPoint): { from: HaulPoint; to: HaulPoint } {
  return {
    from: isStop(a) ? bay(a, b) : { x: a.x, z: a.z },
    to: isStop(b) ? bay(b, a) : { x: b.x, z: b.z },
  };
}

/** Every run the state implies on this plot, transfers first, at most `HAUL_CAP`. */
export function haulRoutes(src: HaulSources): HaulRoute[] {
  const out: HaulRoute[] = [];
  for (const t of src.transfers) {
    const a = endOf(t.from, src);
    const b = endOf(t.to, src);
    if (!a || !b || a === b) continue;
    const key = `t:${t.id}`;
    out.push({ key, kind: 'transfer', ...leg(a, b), startedAt: t.startedAt, readyAt: t.readyAt, bow: hash01(key) < 0.5 ? -1 : 1 });
  }
  for (const job of src.jobs) {
    if (!src.running(job)) continue;
    const at = src.stops.find((s) => s.buildingId === job.buildingId);
    if (!at) continue;
    const inputs = src.inputsOf(job.recipeId);
    if (inputs.length === 0) continue;
    // The nearest building on the plot that makes any of its inputs; else the pile.
    let from: HaulStop | HaulPoint = src.pile;
    let best = Infinity;
    for (const res of inputs) {
      const makers = src.makersOf(res);
      for (const s of src.stops) {
        if (s === at || !s.kind || !makers.includes(s.kind)) continue;
        const d = Math.hypot(s.x - at.x, s.z - at.z);
        if (d < best) { best = d; from = s; }
      }
    }
    const key = `f:${job.id}`;
    out.push({ key, kind: 'feed', ...leg(from, at), startedAt: 0, readyAt: 0, bow: hash01(key) < 0.5 ? -1 : 1 });
  }
  return out.slice(0, HAUL_CAP);
}

/** Where a run's hauler stands: along its track 0..1, which way it faces, and whether it carries a load. */
export interface HaulPose {
  x: number;
  z: number;
  /** Heading on the plot, radians about +y, 0 along +z. */
  heading: number;
  loaded: boolean;
  /** Moving, not standing at an end. */
  moving: boolean;
}

/** The track: a shallow arc between the ends, so two runs on one line do not overlap. */
export function trackAt(r: HaulRoute, s: number): { x: number; z: number; dx: number; dz: number } {
  const mx = (r.from.x + r.to.x) / 2;
  const mz = (r.from.z + r.to.z) / 2;
  const ex = r.to.x - r.from.x;
  const ez = r.to.z - r.from.z;
  const cx = mx - ez * 0.12 * r.bow;
  const cz = mz + ex * 0.12 * r.bow;
  const u = 1 - s;
  return {
    x: u * u * r.from.x + 2 * u * s * cx + s * s * r.to.x,
    z: u * u * r.from.z + 2 * u * s * cz + s * s * r.to.z,
    dx: 2 * u * (cx - r.from.x) + 2 * s * (r.to.x - cx),
    dz: 2 * u * (cz - r.from.z) + 2 * s * (r.to.z - cz),
  };
}

/** A smooth start and stop over a run's fraction. */
function ease(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Where a run's hauler is at `now` (ms). */
export function haulPose(r: HaulRoute, now: number): HaulPose {
  let s: number;
  let loaded = true;
  let moving = true;
  let back = false;
  if (r.kind === 'transfer') {
    // The span is the server's clock and `now` this client's: skew moves
    // where along its trip a hauler stands, never whether it is drawn. The
    // state carries no server time to correct it against.
    const span = Math.max(1, r.readyAt - r.startedAt);
    const f = Math.min(1, Math.max(0, (now - r.startedAt) / span));
    s = ease(f);
    moving = f > 0 && f < 1;
  } else {
    const len = Math.hypot(r.to.x - r.from.x, r.to.z - r.from.z) * 1.02;
    const drive = Math.max(4, len / HAUL_SPEED_MS);
    const cycle = 2 * drive + 2 * HAUL_DWELL_S;
    const t = ((now / 1000 + hash01(r.key) * cycle) % cycle + cycle) % cycle;
    if (t < HAUL_DWELL_S) { s = 0; moving = false; } else if (t < HAUL_DWELL_S + drive) {
      s = ease((t - HAUL_DWELL_S) / drive);
    } else if (t < 2 * HAUL_DWELL_S + drive) { s = 1; moving = false; loaded = false; } else {
      s = 1 - ease((t - 2 * HAUL_DWELL_S - drive) / drive);
      loaded = false;
      back = true;
    }
  }
  const p = trackAt(r, s);
  const heading = Math.atan2(back ? -p.dx : p.dx, back ? -p.dz : p.dz);
  return { x: p.x, z: p.z, heading, loaded, moving };
}

/**
 * The site pile's spot: the plot's origin, the site itself, unless a pad
 * covers it; then the nearest point on rings around it that no pad does.
 */
export function pileSpot(stops: readonly HaulStop[]): HaulPoint {
  const clear = (x: number, z: number): boolean =>
    stops.every((s) => Math.hypot(s.x - x, s.z - z) > s.halfM * 1.15 + 40);
  if (clear(0, 0)) return { x: 0, z: 0 };
  for (let r = 60; r <= 1200; r += 60) {
    for (let k = 0; k < 16; k++) {
      const a = (k / 16) * Math.PI * 2;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      if (clear(x, z)) return { x, z };
    }
  }
  return { x: 0, z: 0 };
}
